import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpRequest } from '@angular/common/http';
import { Observable, throwError, concat } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppSettings } from '../../utils/app-settings';

export interface UploadProgress {
  progress: number;
  loaded: number;
  total: number;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  data?: any;
}

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  constructor(
    private http: HttpClient,
    private appSettings: AppSettings
  ) {}

  /**
   * Upload file for returns
   */
  uploadReturnFile(
    returnId: number,
    file: File
  ): Observable<HttpEvent<UploadResponse>> {
    const url = this.appSettings.returns.urls.uploadFile(returnId) + '?source=backoffice';
    return this.uploadFile(url, file, 'files');
  }

  /**
   * Upload file for shipping
   */
  uploadShippingFile(
    shippingId: number,
    receiptId: number,
    file: File
  ): Observable<HttpEvent<UploadResponse>> {
    const url = this.appSettings.shipping.urls.uploadFile(shippingId, receiptId) + '?source=backoffice';
    return this.uploadFile(url, file, 'files');
  }

  /**
   * Upload multiple files for shipping
   */
  uploadShippingFiles(
    shippingId: number,
    receiptId: number,
    files: File[]
  ): Observable<HttpEvent<UploadResponse>> {
    const url = this.appSettings.shipping.urls.uploadFile(shippingId, receiptId) + '?source=backoffice';
    return this.uploadMultipleFiles(url, files, 'files');
  }

  /**
   * Generic multiple file upload method
   */
  private uploadMultipleFiles(
    url: string,
    files: File[],
    fieldName: string = 'files'
  ): Observable<HttpEvent<UploadResponse>> {
    const requests$ = files.map(file => {
      const formData = new FormData();
      formData.append(fieldName, file, file.name);

      const request = new HttpRequest('POST', url, formData, {
        reportProgress: true,
        responseType: 'json',
      });

      return this.http.request<UploadResponse>(request);
    });

    return concat(...requests$).pipe(
      catchError((error) => {
        const errorMessage = error.error?.message || 'Error al subir los archivos';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Generic file upload method
   */
  private uploadFile(
    url: string,
    file: File,
    fieldName: string = 'file'
  ): Observable<HttpEvent<UploadResponse>> {
    const formData = new FormData();
    formData.append(fieldName, file, file.name);

    const request = new HttpRequest('POST', url, formData, {
      reportProgress: true,
      responseType: 'json',
    });

    return this.http.request<UploadResponse>(request).pipe(
      catchError((error) => {
        const errorMessage = error.error?.message || 'Error al subir el archivo';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Extract upload progress from HttpEvent
   */
  getUploadProgress(event: HttpEvent<any>): UploadProgress | null {
    if (event.type === HttpEventType.UploadProgress && event.total) {
      const progress = Math.round((100 * event.loaded) / event.total);
      return {
        progress,
        loaded: event.loaded,
        total: event.total,
      };
    }
    return null;
  }

  /**
   * Check if upload is complete
   */
  isUploadComplete(event: HttpEvent<any>): boolean {
    return event.type === HttpEventType.Response;
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File, maxSizeMB: number = 10): { valid: boolean; error?: string } {
    // Check file size (convert MB to bytes)
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `El archivo es muy grande. Tamaño máximo: ${maxSizeMB}MB`,
      };
    }

    // Check file type (allow common document and image formats)
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de archivo no permitido. Use PDF, imágenes o documentos Word.',
      };
    }

    return { valid: true };
  }
} 